/**
 * Script injected into the preview iframe to enable element selection.
 * Communicates with the parent via postMessage.
 *
 * This script is injected into index.html via action-runner.ts when files are written
 * to the WebContainer, bypassing cross-origin iframe restrictions.
 * The script tag uses this id so repeated writes do not stack duplicate injections.
 */
export const CANVAS_OVERLAY_SCRIPT_ID_PREFIX = 'dualite-canvas-overlay';
export const CANVAS_OVERLAY_SCRIPT_ID = 'dualite-canvas-overlay-v1'; // change when you make changes in the injected script

/**
 * Inline Babel plugin function that annotates every JSX element with a
 * `data-ds="filePath:line:column"` attribute at compile time.
 *
 * Injected into `@vitejs/plugin-react`'s babel config via action-runner.ts
 * when writing vite.config files.  Only affects compiled output served to
 * the browser — source files on disk remain untouched.
 */
export const DUALITE_SOURCE_LOC_BABEL_PLUGIN = `function __dualiteSourceLoc({ types: t }) {
  return { visitor: { JSXOpeningElement(path, state) {
    var fn = state.filename || '';
    if (!fn || fn.includes('node_modules')) return;
    var name = path.node.name;
    var reactSpecials = ['Fragment', 'StrictMode', 'Suspense', 'Profiler'];
    var isReactSpecial = (name.type === 'JSXIdentifier' && reactSpecials.indexOf(name.name) !== -1) ||
      (name.type === 'JSXMemberExpression' && name.object && name.object.name === 'React' && name.property && reactSpecials.indexOf(name.property.name) !== -1) ||
      (name.type === 'JSXMemberExpression' && name.property && (name.property.name === 'Provider' || name.property.name === 'Consumer'));
    if (isReactSpecial) return;
    var attrs = path.node.attributes;
    for (var i = 0; i < attrs.length; i++) {
      if (attrs[i].type === 'JSXAttribute' && attrs[i].name && attrs[i].name.name === 'data-ds') return;
    }
    var loc = path.node.loc;
    if (!loc) return;
    var wd = '/home/project/';
    var rel = fn.startsWith(wd) ? fn.slice(wd.length) : fn;
    attrs.push(t.jsxAttribute(t.jsxIdentifier('data-ds'), t.stringLiteral(rel + ':' + loc.start.line + ':' + loc.start.column)));
  } } };
}`;

/** Marker used to detect whether the Babel plugin was already injected into a vite config. */
export const DUALITE_SOURCE_LOC_MARKER = '__dualiteSourceLoc';

export const CANVAS_OVERLAY_SCRIPT = `
(function() {
  if (window.__canvasOverlayInit) return;
  window.__canvasOverlayInit = true;

  try {

  var enabled = false;
  let hoveredEl = null;
  let selectedEl = null;

  function getSelector(el) {
    if (el.id) return '#' + el.id;
    let path = [];
    while (el && el !== document.body && el !== document.documentElement) {
      let tag = el.tagName.toLowerCase();
      let sibling = el;
      let nth = 1;
      while (sibling.previousElementSibling) {
        sibling = sibling.previousElementSibling;
        if (sibling.tagName === el.tagName) nth++;
      }
      path.unshift(nth > 1 ? tag + ':nth-of-type(' + nth + ')' : tag);
      el = el.parentElement;
    }
    return path.join(' > ');
  }

  function getRect(el) {
    const r = el.getBoundingClientRect();
    return { top: r.top, left: r.left, width: r.width, height: r.height };
  }

  function getComputedProps(el) {
    var cs = window.getComputedStyle(el);
    return {
      tagName: el.tagName.toLowerCase(),
      id: el.id || '',
      className: (typeof el.className === 'string') ? el.className : '',
      textContent: (el.childNodes.length === 1 && el.childNodes[0].nodeType === 3) ? (el.textContent || '').slice(0, 80) : '',
      fontSize: cs.fontSize,
      fontWeight: cs.fontWeight,
      fontFamily: cs.fontFamily,
      color: cs.color,
      backgroundColor: cs.backgroundColor,
      opacity: cs.opacity,
      textAlign: cs.textAlign,
      padding: cs.padding,
      margin: cs.margin,
      borderRadius: cs.borderRadius,
      borderWidth: cs.borderWidth,
      borderColor: cs.borderColor,
      width: cs.width,
      height: cs.height,
      display: cs.display,
    };
  }

  function parseDataDs(ds) {
    var parts = ds.split(':');
    if (parts.length < 3) return null;
    var col = parseInt(parts[parts.length - 1], 10);
    var line = parseInt(parts[parts.length - 2], 10);
    var filePath = parts.slice(0, parts.length - 2).join(':');
    if (!filePath || isNaN(line)) return null;
    return { sourceFilePath: filePath, sourceLine: line, sourceColumn: isNaN(col) ? null : col };
  }

  function getDebugSource(el) {
    var node = el;
    while (node && node !== document.documentElement) {
      if (node.getAttribute) {
        var ds = node.getAttribute('data-ds');
        if (ds) {
          var parsed = parseDataDs(ds);
          if (parsed) return parsed;
        }
      }
      node = node.parentElement;
    }

    var workDir = '/home/project/';
    function strip(f) {
      if (!f) return '';
      return f.startsWith(workDir) ? f.slice(workDir.length) : f;
    }

    try {
      var vNode = el;
      while (vNode && vNode !== document.documentElement) {
        var vc = vNode.__vueParentComponent;
        if (vc && vc.type && vc.type.__file) {
          return { sourceFilePath: strip(vc.type.__file), sourceLine: null, sourceColumn: null };
        }
        vNode = vNode.parentElement;
      }
    } catch (err) {
      console.warn('[canvas-overlay] Vue fallback error:', err);
    }

    return null;
  }

  function clearOverlayState() {
    hoveredEl = null;
    selectedEl = null;
    window.parent.postMessage({ type: 'canvas-hover-clear' }, '*');
  }

  document.addEventListener('mousemove', function(e) {
    if (!enabled) return;
    const el = document.elementFromPoint(e.clientX, e.clientY);
    if (!el || el === hoveredEl) return;
    hoveredEl = el;
    window.parent.postMessage({
      type: 'canvas-hover',
      rect: getRect(el),
      selector: getSelector(el),
    }, '*');
  }, true);

  document.addEventListener('click', function(e) {
    if (!enabled) return;
    e.preventDefault();
    e.stopPropagation();
    const el = document.elementFromPoint(e.clientX, e.clientY);
    if (!el) return;
    selectedEl = el;
    var source = getDebugSource(el);
    window.parent.postMessage({
      type: 'canvas-select',
      rect: getRect(el),
      selector: getSelector(el),
      props: getComputedProps(el),
      source: source,
    }, '*');
  }, true);

  var syncSelectRaf = 0;
  function postSelectedRectIfAny() {
    if (!selectedEl) return;
    if (!selectedEl.isConnected) {
      selectedEl = null;
      return;
    }
    window.parent.postMessage({
      type: 'canvas-select-rect',
      rect: getRect(selectedEl),
    }, '*');
  }
  function scheduleSelectedRectSync() {
    if (!enabled) return;
    if (!selectedEl) return;
    if (syncSelectRaf) return;
    syncSelectRaf = requestAnimationFrame(function() {
      syncSelectRaf = 0;
      postSelectedRectIfAny();
    });
  }
  window.addEventListener('scroll', scheduleSelectedRectSync, true);
  window.addEventListener('resize', scheduleSelectedRectSync);

  document.addEventListener('mouseleave', function() {
    if (!enabled) return;
    hoveredEl = null;
    window.parent.postMessage({ type: 'canvas-hover-clear' }, '*');
  });

  window.addEventListener('message', function(e) {
    if (e.source !== window.parent) return;
    if (!e.data) return;
    if (e.data.type === 'canvas-enable') {
      enabled = true;
    } else if (e.data.type === 'canvas-disable') {
      enabled = false;
      clearOverlayState();
    } else if (e.data.type === 'canvas-deselect') {
      selectedEl = null;
    }
  });

  window.parent.postMessage({ type: 'canvas-overlay-ready' }, '*');

  } catch (err) {
    window.parent.postMessage({ type: 'canvas-overlay-error', message: String(err) }, '*');
  }
})();
`;
